import React, { useEffect, useState, useRef } from "react";
import {
    Typography,
    Button,
    Input,
    IconButton,
    Tooltip
} from "@material-tailwind/react";
import Toaster from "../../components/Toaster";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Popover } from 'react-tiny-popover';
import ShowLoader from "@/components/Loader";
import {
    Bars3BottomLeftIcon,
    MagnifyingGlassIcon,
    XCircleIcon,
    InformationCircleIcon,
    ClockIcon,
    ChatBubbleOvalLeftEllipsisIcon,
    DocumentArrowDownIcon,
    FunnelIcon,
} from "@heroicons/react/24/solid";
import ScrollToTop from "@/components/ScrollToTop";
import TimeLineModal from "./order-modals/timeline-modal";
import moment from "moment";
import InfiniteScroll from 'react-infinite-scroll-component';
import { clearOrders, getOrderCount, getCompletedOrders } from "@/store/slices/orderSlice";
import DocumentViewModal from "@/components/DocumentViewModal";
import { useTranslation } from "react-i18next";

let action_options = [];

export function CompletedDeliveryOrder() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { t } = useTranslation('order');
    const { t: ct } = useTranslation('common');

    const { user } = useSelector((state) => state.user);
    const { list_completed_orders, loading, list_loading, hasMore, total_count, order_counts } = useSelector((state) => state.order);

    const [search, setSearch] = useState('');
    const [sortingOrder, setSortingOrder] = useState('');
    const [isSortingPopoverOpen, setIsSortingPopoverOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [activeTab, setActiveTab] = useState("normal");
    const [isTimelineModalOpen, setIsTimelineModalOpen] = useState(false);
    const [statusFilter, setStatusFilter] = useState('');
    const [filter, setFilter] = useState('completed_orders');
    const [isSpinning1, setIsSpinning1] = useState(false);
    const [isSetPOFile, setPOFile] = useState(false);
    const [orderTypeFilter, setOrderTypeFilter] = useState('');
    const [isFilterPopoverOpen, setIsFilterPopoverOpen] = useState(false);

    const sorting_options = [
        { label: t('sort_ascending'), value: 'asc' },
        { label: t('sort_descending'), value: 'desc' },
    ];

    const [tabs, setTabs] = useState([{ id: "completed_orders", label: t('completed_deliveries_tab') }]);

    useEffect(() => {
        dispatch(getOrderCount());
    }, [])

    const loadData = (pageNumber = 1, isAppend = false) => {
        dispatch(getCompletedOrders({
            activetab: activeTab,
            page: pageNumber,
            perPage: itemsPerPage,
            search: search,
            filter: filter,
            sort_order: sortingOrder,
            status: statusFilter,
            dashboard_key: orderTypeFilter
        }));
    };

    useEffect(() => {
        if (!filter) return;

        setCurrentPage(1);

        dispatch(clearOrders());
        let params = {};
        params.filter = filter;
        params.activeTab = activeTab;
        setSearchParams(params, { replace: true });
        loadData(1);

    }, [activeTab, filter, sortingOrder, statusFilter, orderTypeFilter]);


    // Handlers
    const handelSearch = (event) => {
        if (event.key === "Enter") {
            setCurrentPage(1);
            loadData(1);
        } else if (event.key === "Backspace" && search.length === 0) {
            setCurrentPage(1);
            loadData(1);
        }
    };

    const handleSearchClick = () => {
        setCurrentPage(1);
        loadData(1);
    };

    const handelRemoveSearch = () => {
        setSearch('');
        setCurrentPage(1);
        dispatch(getCompletedOrders({
            activetab: activeTab, page: 1, perPage: itemsPerPage, search: '', filter, sort_order: sortingOrder, status: statusFilter,
            dashboard_key: orderTypeFilter
        }));
    };

    const handelSorting = (value) => {
        setSortingOrder(value);
        setIsSortingPopoverOpen(false);
    };

    const fetchMoreData = () => {
        if (!list_loading && hasMore) {
            const nextPage = currentPage + 1;
            dispatch(getCompletedOrders({
                activetab: activeTab,
                page: nextPage,
                perPage: itemsPerPage,
                search: search,
                filter: filter,
                sort_order: sortingOrder,
                status: statusFilter,
                dashboard_key: orderTypeFilter
            })).unwrap().then(() => {
                setCurrentPage(nextPage);
            }).catch(() => {
                // Handle error silently or show toast
            });
        }
    };

    const forceReloadOrder = () => {
        setIsSpinning1(true);
        setSearch('');
        setCurrentPage(1);
        dispatch(getCompletedOrders({
            activetab: activeTab, page: 1, perPage: itemsPerPage, search: '', filter, sort_order: '', status: '',
            dashboard_key: orderTypeFilter
        }))
            .finally(() => setTimeout(() => setIsSpinning1(false), 1000));
    };

    const formatReceivedOrderDate = (dateStr) => {
        if (!dateStr) return "N/A";
        const date = moment(dateStr);
        return date.isSame(moment(), "day") ? `${t('today_date')} | ${date.format("h:mm A")}` : date.format("D MMM | h:mm A");
    };


    const [openRemarkId, setOpenRemarkId] = useState(null);

    const getTimeSlotStatus = (toTime) => {
        if (!toTime) return { color: "text-gray-500", text: t('na_status'), delayed: "" };

        const now = moment();
        const end = moment(toTime);
        const isDelayed = now.isAfter(end);

        let delayedText = "";
        if (isDelayed) {
            const days = now.diff(end, 'days');
            const hours = now.diff(end, 'hours') % 24;

            if (days > 0) { delayedText = ` (${days} ${days === 1 ? t('day_delayed') : t('days_delayed')})`; }
            else if (hours > 0) { delayedText = ` (${hours} ${hours === 1 ? t('hour_delayed') : t('hours_delayed')})`; }
            else { delayedText = ` (${t('delayed_status')})`; }
        }

        return {
            color: isDelayed ? "text-red-500" : "text-green-500",
            delayed: delayedText
        };
    };

    return (
        <div className="mt-5 mb-8 flex flex-col gap-8">
            {loading && <ShowLoader />}
            <ScrollToTop />

            {/* Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 px-4 md:px-6">
                <div className="flex w-full md:flex-1 items-center">
                    <Typography variant="h5" color="blue-gray" className="font-bold text-xl">
                        {t('completed_delivery_order_list_title')}
                    </Typography>
                    <button onClick={forceReloadOrder} className="ml-4">
                        <img src="/img/icons/reload.png" alt={ct('reload')} className={`h-4 w-4 ${isSpinning1 ? "animate-spin" : ""}`} />
                    </button>
                </div>

                <div className="w-full md:flex-1 flex justify-center items-center gap-2">
                    <div className="w-full relative max-w-md">
                        <Input
                            label={ct("search")}
                            icon={!search && <MagnifyingGlassIcon className="h-5 w-5" />}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyUp={handelSearch}
                            className="pr-10"
                        />
                        {search && (
                            <IconButton variant="text" size="sm" className="!absolute right-1 top-1" onClick={handelRemoveSearch}>
                                <XCircleIcon className="h-5 w-5 text-red-500" />
                            </IconButton>
                        )}
                    </div>
                    {search && (
                        <Button
                            variant="outlined"
                            className="flex items-center border-gray-300 capitalize font-bold bg-black text-white whitespace-nowrap"
                            onClick={handleSearchClick}
                        >
                            {t('search_button')}
                        </Button>
                    )}
                </div>
            </div>

            <div className="mb-7 grid gap-y-3 gap-x-3 md:grid-cols-2 xl:grid-cols-6 px-4 md:px-6">

                <div className="border border-blue-gray-100 shadow-sm p-4 rounded-xl bg-white">
                    <Typography variant="h4" color="blue-gray">
                        {total_count?.completed_orders ?? 0}
                    </Typography>
                    <Typography variant="small" className="font-normal text-blue-gray-600 mt-2">
                        {t('completed_orders_label')}
                    </Typography>
                </div>
            </div>

            {/* Tab Toggle (Normal vs Custom) */}
            <div className="flex flex-row justify-between items-center px-0 xl:px-6 w-full max-w-[1000px] xl:max-w-full overflow-x-auto">
                <div className="flex bg-gray-100 w-fit rounded-2xl p-1 shadow-sm gap-2 shrink-0 pr-2 xl:pr-0">
                    <button
                        onClick={() => setActiveTab("normal")}
                        className={`relative text-sm font-normal px-4 md:px-6 py-2 rounded-2xl transition-all duration-300 z-10 ${activeTab === "normal" ? "text-white bg-black" : "text-gray-700 hover:text-black"}`}
                    >
                        {t('normal_order_tab')}
                    </button>
                    <button
                        onClick={() => setActiveTab("custom")}
                        className={`relative text-sm font-normal px-4 md:px-6 py-2 rounded-2xl transition-all duration-300 z-10 ${activeTab === "custom" ? "text-white bg-black" : "text-gray-700 hover:text-black"}`}
                    >
                        {t('custom_order_tab')}
                    </button>
                </div>
                <div className="flex items-center gap-3">
                    <Popover
                        isOpen={isSortingPopoverOpen}
                        positions={["bottom"]}
                        onClickOutside={() => setIsSortingPopoverOpen(false)}
                        content={
                            <div className="bg-white shadow-xl rounded-md w-48 flex flex-col z-50 py-1 border border-gray-100">
                                <button onClick={() => handelSorting("")} className={`text-left text-sm px-3 py-2 hover:bg-gray-100 ${sortingOrder === '' ? 'bg-gray-50' : ''}`}>{t('latest_sort')}</button>
                                {sorting_options.map((opt) => (
                                    <button key={opt.value} onClick={() => handelSorting(opt.value)} className={`text-left text-sm px-3 py-2 hover:bg-gray-100 ${sortingOrder === opt.value ? 'bg-gray-50' : ''}`}>{opt.label}</button>
                                ))}
                            </div>
                        }
                    >
                        <Button onClick={() => setIsSortingPopoverOpen(!isSortingPopoverOpen)} variant="outlined" className="flex items-center gap-2 bg-[#EBE9E6] border-none text-gray-800 capitalize">
                            <Bars3BottomLeftIcon className="h-4 w-4" />
                        </Button>
                    </Popover>
                    {/* ✅ NEW — Delivery Partner Payment filter */}
                    <Popover
                        isOpen={isFilterPopoverOpen}
                        positions={["bottom"]}
                        onClickOutside={() => setIsFilterPopoverOpen(false)}
                        content={
                            <div className="bg-white shadow-xl rounded-md w-48 flex flex-col z-50 border border-gray-100 py-1">
                                <button className="text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2 capitalize" onClick={() => { setOrderTypeFilter(''); setIsFilterPopoverOpen(false); }}>{t('all_filter')}</button>
                                <p className="px-3 pt-1 pb-0.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                                    {t('delivery_partner_payment_heading')}
                                </p>
                                <button className="text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2 capitalize" onClick={() => { setOrderTypeFilter('payment_pending'); setIsFilterPopoverOpen(false); }}>{t('payment_pending_label')}</button>
                                <button className="text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2 capitalize" onClick={() => { setOrderTypeFilter('partially_paid'); setIsFilterPopoverOpen(false); }}>{t('partially_paid_label')}</button>
                                <button className="text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2 capitalize" onClick={() => { setOrderTypeFilter('fully_paid'); setIsFilterPopoverOpen(false); }}>{t('fully_paid_label')}</button>
                            </div>
                        }
                    >
                        <Button
                            onClick={() => setIsFilterPopoverOpen(!isFilterPopoverOpen)}
                            variant="outlined"
                            className="flex items-center gap-2 border-gray-300 capitalize"
                        >
                            <FunnelIcon className="h-4 w-4" />
                            {t('filter_button')} {orderTypeFilter ? `(${orderTypeFilter.replace('_', ' ')})` : ''}
                        </Button>
                    </Popover>

                </div>
            </div>
            <div className="flex gap-4 md:gap-8 border-b border-gray-100 px-4 md:px-6 overflow-x-auto">
                {tabs.map((t) => (
                    <button
                        key={t.id}
                        onClick={() => setFilter(t.id)}
                        className={`pb-2 text-sm md:text-base font-medium transition-all whitespace-nowrap ${filter === t.id ? "border-b-2 border-black text-black" : "text-gray-500 hover:text-gray-700"}`}
                    >
                        {t.label} {(total_count && total_count[t.id]) ? `(${total_count[t.id]})` : '(0)'}
                    </button>
                ))}
            </div>

            {/* Data List Container */}
            <div className="px-4 md:px-6">
                {list_loading && currentPage === 1 && list_completed_orders?.length === 0 ? (
                    <div className="flex justify-center items-center py-20">
                        <ShowLoader />
                    </div>
                ) : list_completed_orders?.length === 0 && !list_loading ? (
                    <div className="flex flex-col items-center justify-center py-20 min-h-[400px]">
                        <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p className="text-center text-gray-400 text-base font-medium">{t('no_deliveries_found')}</p>
                        <p className="text-center text-gray-400 text-sm mt-2">{t('try_adjusting_filters')}</p>
                    </div>
                ) : (
                    <div id="scrollableDiv" className="divide-y divide-gray-100 h-[calc(100vh-350px)] min-h-[400px] overflow-y-auto overflow-x-auto">
                        <InfiniteScroll
                            dataLength={list_completed_orders?.length || 0}
                            next={fetchMoreData}
                            hasMore={hasMore}
                            scrollableTarget="scrollableDiv"
                            loader={
                                <div className="flex justify-center items-center py-4">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                                    <span className="ml-2 text-gray-600 text-sm">{t('loading_more')}</span>
                                </div>
                            }
                        >
                            {list_completed_orders?.length > 0 && list_completed_orders?.map((order) => {
                                const totalPrice = order?.products?.reduce((sum, item) => sum + (item?.customer_price ? Number(item.customer_price) : Number(item.total_amount) || 0), 0);
                                const formattedPrice = totalPrice ? totalPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00';

                                return (
                                    <div key={order.id} className="grid grid-cols-6 w-full min-w-[1100px] items-start gap-4 pt-7 pb-5 hover:bg-gray-50 transition-colors border-b border-[#DBDBDB]">
                                        {/* ID & Checkbox */}
                                        <div className="flex items-start gap-3">

                                            <div className="flex flex-col gap-1">
                                                {(() => {
                                                    const isFinance = user?.roles[0] === 'Finance';
                                                    const isPending = filter === 'pending_for_approval';

                                                    const shouldDisable = isPending && !isFinance;

                                                    if (shouldDisable) {
                                                        return (
                                                            <span className="font-bold text-sm text-[#1A1918]">
                                                                {t('po_id_label')}: {order.order_id}
                                                            </span>
                                                        );
                                                    }

                                                    return (
                                                        <Link
                                                            to={`/dashboard/completed-delivery-orders/${order.id}?rfq_id=${order?.rfq_id}`}
                                                            state={{ from: searchParams.toString() }}
                                                            className="font-bold text-sm text-[#1A1918] hover:text-blue-600 hover:underline cursor-pointer transition"
                                                        >
                                                            {t('po_id_label')}: {order.order_id}
                                                        </Link>
                                                    );
                                                })()}
                                                <p className="text-sm text-gray-500 font-medium flex items-center gap-2">
                                                    <span>{t('po_label')}: {order.purchase_order_number}</span>

                                                    {order?.purchase_order_file && (
                                                        <DocumentArrowDownIcon
                                                            className="h-4 w-4 text-gray-500 cursor-pointer hover:text-blue-600"
                                                            onClick={() => setPOFile(order.purchase_order_file)}
                                                        />
                                                    )}
                                                </p>
                                                <p className="text-sm text-gray-500 font-medium flex items-center gap-2">
                                                    <span>{t('cs_rfq_ref_id_label')}: {order?.rfq?.rfq_reference}</span>
                                                </p>

                                            </div>
                                        </div>

                                        {/* Customer Info */}
                                        <div className="flex flex-col gap-1">
                                            <div className="flex items-center gap-1">
                                                <p className="font-bold text-sm text-[#1A1918]">{order?.customer?.company_name}</p>
                                                {order?.alternate_number && (
                                                    <Tooltip content={`${t('alt_phone_tooltip')} ${order.alternate_number}`} className="bg-white text-gray-900 border border-gray-200 shadow-md">
                                                        <InformationCircleIcon className="h-4 w-4 text-gray-400 cursor-pointer" />
                                                    </Tooltip>
                                                )}
                                            </div>
                                            <p className="text-sm font-medium text-gray-500 m-0">{order?.customer?.country_code} {order?.customer?.mobile_number}</p>

                                            {user?.roles[0] === 'Finance' && <>
                                                <div className="flex items-center gap-1">
                                                    <p className="font-bold text-sm text-[#1A1918]">{t('due_payment_label')}</p>
                                                </div>
                                                <p className="text-sm font-medium text-gray-500 m-0">SAR {order?.payment_summary?.due_amount ?? 0}</p>
                                            </>
                                            }

                                        </div>

                                        {/* Items & Location */}
                                        <div className="flex flex-col gap-1">
                                            <p className="font-bold text-sm text-[#1A1918]">{t('items_label')}: {order?.products?.length || 0}</p>
                                            <p className="text-sm font-medium text-gray-500 truncate">{t('deliver_to_label')} {order?.customer?.kyc_details?.permanent_address || t('na_status')}</p>
                                            {user?.roles[0] === 'Finance' && <>
                                                <div className="flex items-center gap-1">
                                                    <p className="font-bold text-sm text-[#1A1918]">{t('amount_received_label')}</p>
                                                </div>
                                                <p className="text-sm font-medium text-gray-500 m-0">SAR {order?.payment_summary?.paid_amount ?? 0}</p>
                                            </>
                                            }

                                        </div>

                                        {/* Pricing & Deadline */}
                                        <div className="flex flex-col gap-1">
                                            <p className="text-[#1A1918] font-bold">SAR {order?.payment_summary?.grand_total ?? 0}</p>
                                            {(user?.roles[0] === 'Sub Admin' || user?.roles[0] === 'Administrator' || user?.roles[0] === 'Finance') &&
                                                <p className="text-sm font-medium text-gray-500">
                                                    {t('deadline_label')} {order?.assigned_details?.[0]?.deadline ? moment(order.assigned_details[0].deadline).format("D MMM | h:mm A") : t('na_status')}
                                                </p>
                                            }
                                            {(user?.roles[0] !== 'Sub Admin' && user?.roles[0] !== 'Administrator' && user?.roles[0] !== 'Finance') && (
                                                (() => {
                                                    const assigned_user = order?.assigned_details?.find((item) => item.assigned_to_id === user?.id) ||
                                                        order?.assigned_details?.[0];

                                                    const timeSlotTo = assigned_user?.timeslot?.to;
                                                    const status = getTimeSlotStatus(timeSlotTo);
                                                    return (
                                                        <p className={`text-sm font-medium ${status.color}`}>
                                                            {t('timeslot_label')} {assigned_user?.timeslot?.from ?
                                                                moment(assigned_user.timeslot.from).format("D MMM | h:mm A") + " - " + moment(timeSlotTo).format("D MMM | h:mm A")
                                                                : t('na_status')
                                                            }
                                                            {status.delayed}
                                                        </p>
                                                    );
                                                })()
                                            )}
                                            {user?.roles[0] === 'Finance' && <>
                                                <div className="flex items-center gap-1">
                                                    <p className="font-bold text-sm text-[#1A1918]">{t('last_amount_label')}</p>
                                                </div>
                                                <p className="text-sm font-medium text-gray-500 m-0">
                                                    SAR {order?.payment_summary?.last_paid_amount ?? 0}{" "}
                                                    {order?.payment_summary?.updated_at
                                                        ? "Received On: " + order.payment_summary.updated_at
                                                        : ""}
                                                </p>
                                            </>
                                            }

                                        </div>

                                        {/* Actions Icons */}
                                        <div className="flex flex-col gap-2 items-center justify-center">
                                            <div className="flex gap-2 justify-center items-center">
                                                {(() => {
                                                    const isFinance = user?.roles[0] === 'Finance';
                                                    const isPending = filter === 'pending_for_approval';

                                                    const shouldHide = isPending && !isFinance;

                                                    if (shouldHide) return null;

                                                    return (
                                                        <>
                                                            {/* Timeline Icon */}
                                                            <Tooltip content={t('timeline_tooltip')}>
                                                                <ClockIcon className="w-4 h-4 text-gray-500 cursor-pointer hover:text-black" onClick={() => setIsTimelineModalOpen(order.id)} />
                                                            </Tooltip>

                                                            {/* Chat Icon */}
                                                            <Tooltip content={t('chat_tooltip')}>
                                                                <Link to={`/dashboard/orders/${order.id}`} state={{ active_tab: 'chat' }}>
                                                                    <ChatBubbleOvalLeftEllipsisIcon className="w-4 h-4 text-gray-500 hover:text-blue-500" />
                                                                </Link>
                                                            </Tooltip>
                                                        </>
                                                    );
                                                })()}
                                                {/* Remark/Notes Popover */}
                                                {order?.assigned_details?.some(d => d.notes && d.assigned_to?.id === user?.id) && (
                                                    <Popover
                                                        isOpen={openRemarkId === order.id}
                                                        positions={["left", "bottom", "top"]}
                                                        padding={10}
                                                        onClickOutside={() => setOpenRemarkId(null)}
                                                        containerStyle={{ zIndex: '9999' }}
                                                        content={
                                                            <div className="bg-white shadow-2xl rounded-lg w-72 border border-gray-200 overflow-hidden">
                                                                <div className="bg-gray-50 px-3 py-2 border-b border-gray-200 flex justify-between items-center">
                                                                    <Typography className="font-bold text-xs text-gray-700">{t('your_remarks_label')}</Typography>
                                                                    <XCircleIcon className="h-4 w-4 text-gray-400 cursor-pointer" onClick={() => setOpenRemarkId(null)} />
                                                                </div>
                                                                <div className="max-h-48 overflow-y-auto p-3 flex flex-col gap-3">
                                                                    {/* Filtered mapping: Only show notes where assigned_to.id matches logged-in user.id */}
                                                                    {order.assigned_details
                                                                        .filter(detail => detail.notes && detail.assigned_to?.id === user?.id)
                                                                        .map((detail, idx) => (
                                                                            <div key={idx} className="flex flex-col gap-1 border-b border-gray-50 last:border-none pb-2">
                                                                                <div className="flex items-center gap-2">
                                                                                    <span className="text-[10px] font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                                                                                        {detail.assigned_to?.name} ({t('you_text')})
                                                                                    </span>
                                                                                    <span className="text-[9px] text-gray-400">
                                                                                        {detail.assigned_at ? moment(detail.assigned_at).format("DD MMM | h:mm A") : ""}
                                                                                    </span>
                                                                                </div>
                                                                                <p className="text-[12px] text-gray-600 leading-snug break-words italic">
                                                                                    "{detail.notes}"
                                                                                </p>
                                                                            </div>
                                                                        ))
                                                                    }
                                                                </div>
                                                            </div>
                                                        }
                                                    >
                                                        <div
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                setOpenRemarkId(openRemarkId === order.id ? null : order.id);
                                                            }}
                                                        >
                                                            <Tooltip content={t('your_remarks_label')}>
                                                                <InformationCircleIcon
                                                                    className={`w-4 h-4 cursor-pointer transition-colors ${openRemarkId === order.id ? "text-blue-600" : "text-gray-500 hover:text-blue-600"}`}
                                                                />
                                                            </Tooltip>
                                                        </div>
                                                    </Popover>
                                                )}
                                            </div>

                                            {/* Status Badges */}
                                            {filter === "assigned" && order?.delivery_status != 'cancelled' && (
                                                <span
                                                    className={`w-20 text-[10px] px-2 py-0.5 rounded-full text-center font-bold
                                                    ${order?.city_representative_approval?.trim() === "yes"
                                                            ? "bg-green-100 text-green-700"
                                                            : order?.city_representative_approval?.trim() === "no"
                                                                ? "bg-red-100 text-red-700"
                                                                : order?.is_send_to_city_rep !== null ? "bg-yellow-100 text-yellow-700" : ''
                                                        }`}
                                                >
                                                    {order?.city_representative_approval?.trim() === "yes" ? t('approved_status')
                                                        : order?.city_representative_approval?.trim() === "no" ? t('rejected_status')
                                                            : order?.is_send_to_city_rep !== null ? t('pending_status')
                                                                : ''
                                                    }
                                                </span>
                                            )}
                                        </div>

                                        {/* Dynamic CTA Button */}
                                        <div className="flex flex-col items-center justify-start right-0 z-20 bg-[#F5F7F8] min-h-[80px] px-2 py-2 shadow-[-8px_0_8px_-6px_rgba(0,0,0,0.1)]">
                                            {(user.roles[0] !== 'Finance' && filter === 'unassigned' && hasUpdatePermission && order?.delivery_status !== 'cancelled') ? (
                                                <button
                                                    onClick={() => {
                                                        if (order?.customer_approval === 'yes') { Toaster.error(t('quotation_already_approved_error')); }
                                                        else {
                                                            setIsAssignModalOpen(order.id)
                                                        }
                                                    }}
                                                    className="bg-[#F4B320] hover:bg-yellow-500 text-[#1A1918] font-normal px-4 py-1.5 rounded-lg transition whitespace-nowrap"
                                                >
                                                    {t('assign_button')}
                                                </button>
                                            ) : (
                                                <div className="flex -space-x-2 justify-center">
                                                    {order?.assigned_details?.filter(r => r.type !== "customer").map((row, i) => (
                                                        <Tooltip key={i} content={row.assigned_to?.name}>
                                                            <img
                                                                className="w-7 h-7 rounded-full border-2 border-white object-cover"
                                                                src={row.assigned_to?.profile_image || '/img/default-avatar-profile-icon.avif'}
                                                                alt={row.assigned_to?.name || 'User'}
                                                                onError={(e) => {
                                                                    e.target.src = '/img/default-avatar-profile-icon.avif';
                                                                }}
                                                            />
                                                        </Tooltip>
                                                    ))}
                                                </div>
                                            )}
                                            {user.roles[0] === 'Finance' && filter === 'pending_for_approval' && hasUpdatePermission &&
                                                <button
                                                    onClick={() => financeApproveOrder(order.id)}
                                                    className="bg-[#F4B320] hover:bg-yellow-500 text-[#1A1918] font-normal px-4 py-1.5 rounded-lg transition whitespace-nowrap"
                                                >
                                                    {t('approve_button')}
                                                </button>
                                            }
                                            {order?.order_status === 'cancellation_requested' && filter === 'cancelled' &&
                                                <button
                                                    onClick={() => handleCancellationAction(order.id)}
                                                    className="bg-[#F4B320] hover:bg-yellow-500 text-[#1A1918] font-normal px-4 py-1.5 rounded-lg transition whitespace-nowrap"
                                                >
                                                    {t('approve_reject_button')}
                                                </button>
                                            }
                                            <p className="text-gray-500 text-[12px] leading-tight mt-2 text-center">{formatReceivedOrderDate(order.created_at)}</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </InfiniteScroll>
                    </div>
                )}
            </div>
            {isTimelineModalOpen && (
                <TimeLineModal
                    isOpen={!!isTimelineModalOpen}
                    onClose={() => setIsTimelineModalOpen(false)}
                    orderId={isTimelineModalOpen}
                />
            )}
            {isSetPOFile && <DocumentViewModal documentUrl={isSetPOFile} setIsDocumentViewModalOpen={setPOFile}></DocumentViewModal>}

        </div>
    );
}

export default CompletedDeliveryOrder;